import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { faqMonitorApi } from '../lib/faq-monitor-api.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  FaqAlfPerformanceQuery,
  FaqDispositionInput,
  FaqDispositionStatus,
  FaqFreshness,
} from '../types/faq-monitor.js';

/**
 * この日数以上、判定が止まっていたら警告を出す（US-7 AC-2）。
 * 日次バッチなので、前日実行（1日）までは正常。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 判定が止まっていると見なす日数のしきい値を定義する
 * @graph-connects none
 */
export const STALE_WARNING_DAYS = 2;

/**
 * 既定で作業リストに出す対応方針。
 * `wont_fix` と `done` は切替で表示する（既定で出すと済んだもので埋まる）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 既定で作業リストに表示する対応方針を定義する
 * @graph-connects none
 */
export const ACTIVE_DISPOSITIONS: readonly FaqDispositionStatus[] = [
  'open',
  'will_fix',
  'spec_wrong',
] as const;

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business FAQ監視の作業リストを取得するフック。判定日を渡すと過去のスナップショットを取得する。
 * @graph-connects faqMonitorApi [calls] FAQ監視 API クライアント
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useFaqWorklist(runDate?: string) {
  return useQuery({
    queryKey: queryKeys.faqMonitor.worklist(runDate),
    queryFn: () => faqMonitorApi.getWorklist(runDate),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * ALF実績を取得するフック。
 *
 * `staleTime` は作業リストより短くする。元データ（問い合わせ）は毎時取り込まれ、
 * 判定日で固定されるスナップショットではないため。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business ALFが引用したFAQ記事ごとの実績を、期間と引用回数の下限を指定して取得するフック
 * @graph-connects faqMonitorApi [calls] ALF実績の取得
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useAlfPerformance(query: FaqAlfPerformanceQuery) {
  return useQuery({
    queryKey: queryKeys.faqMonitor.alfPerformance(query),
    queryFn: () => faqMonitorApi.getAlfPerformance(query),
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 指摘・論点に対する対応方針を保存するミューテーション。成功時に作業リストを再取得する。
 * @graph-connects faqMonitorApi [calls] 対応方針の保存
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useSetDisposition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ hash, input }: { hash: string; input: FaqDispositionInput }) =>
      faqMonitorApi.setDisposition(hash, input),
    onSuccess: () => {
      // 方針を変えると既定の絞り込み対象が変わるため、一覧を取り直す。
      void queryClient.invalidateQueries({ queryKey: ['faq-monitor'] });
    },
  });
}

/**
 * 判定が止まっているか（US-7 AC-2）。
 *
 * 1度も走っていない場合も「止まっている」として扱う。画面を無言で空にすると
 * 「乖離なし」と誤認されるため。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 判定が止まっているかを判断し、画面で警告を出す条件にする
 * @graph-connects none
 */
export function isStale(freshness: FaqFreshness): boolean {
  if (freshness.staleDays === null) return true;
  return freshness.staleDays >= STALE_WARNING_DAYS;
}

/**
 * 既定の作業リストに出す方針か。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 対応方針が既定の作業リストに出る対象かを判断する
 * @graph-connects none
 */
export function isActiveDisposition(status: FaqDispositionStatus): boolean {
  return ACTIVE_DISPOSITIONS.includes(status);
}
